import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { PageHeader, FilterCard, Field, SimpleSelect } from "@/components/common";
import { MONTHS, fmtPct } from "@/lib/helpers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, XCircle, Loader2, Download, AlertTriangle } from "lucide-react";

export default function UploadData() {
  const { indikators, tahuns } = useMeta();
  const [indikator, setIndikator] = useState("");
  const [bulan, setBulan] = useState(String(new Date().getMonth() + 1));
  const [tahun, setTahun] = useState(String(new Date().getFullYear()));
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [targetInfo, setTargetInfo] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!indikator && indikators.length) setIndikator(indikators[0].kode);
  }, [indikators]);

  useEffect(() => {
    if (indikator && tahun) {
      api.get(`/target/check?tahun=${tahun}&indikator=${encodeURIComponent(indikator)}`).then((r) => setTargetInfo(r.data));
    }
  }, [indikator, tahun]);

  const doPreview = async () => {
    if (!file) return toast.error("Pilih file terlebih dahulu");
    setLoading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("indikator", indikator);
      fd.append("bulan", bulan);
      fd.append("tahun", tahun);
      const res = await api.post("/upload/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data);
      toast.success(`Ditemukan ${res.data.jumlah_cocok} baris cocok`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doConfirm = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const rows = preview.matched.map((m) => ({ poliklinik_id: m.poliklinik_id, n: m.n, d: m.d }));
      const res = await api.post("/upload/confirm", {
        indikator: preview.indikator,
        bulan: Number(preview.bulan),
        tahun: Number(preview.tahun),
        nama_file: preview.nama_file,
        rows,
      });
      toast.success(res.data.message);
      setPreview(null);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setConfirming(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "Departemen,Poliklinik,N,D\nPenyakit Dalam,Poli Penyakit Dalam,85,100\nBedah,Poli Bedah Umum,72,90\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "template_upload.csv";
    a.click();
  };

  return (
    <div data-testid="upload-page">
      <PageHeader
        icon={Upload}
        title="Upload Data Bulanan"
        subtitle="Upload file Excel/CSV berisi kolom: Departemen | Poliklinik | N | D. Target tidak perlu diisi ulang tiap bulan."
      >
        <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button">
          <Download className="w-4 h-4 mr-2" /> Template CSV
        </Button>
      </PageHeader>

      <FilterCard>
        <Field label="Indikator">
          <SimpleSelect testId="upload-indikator-select" value={indikator} onChange={setIndikator}
            options={indikators.map((i) => ({ label: i.kode, value: i.kode }))} placeholder="Indikator" className="w-[220px]" />
        </Field>
        <Field label="Bulan">
          <SimpleSelect testId="upload-bulan-select" value={bulan} onChange={setBulan}
            options={MONTHS.map((m) => ({ label: m.nama, value: m.n }))} placeholder="Bulan" className="w-[160px]" />
        </Field>
        <Field label="Tahun">
          <SimpleSelect testId="upload-tahun-select" value={tahun} onChange={setTahun}
            options={[...new Set([...tahuns, Number(tahun)])].sort((a, b) => b - a).map((t) => ({ label: String(t), value: t }))}
            placeholder="Tahun" className="w-[120px]" />
        </Field>
      </FilterCard>

      {targetInfo && !targetInfo.has_target && (
        <Alert className="mb-6 border-amber-300 bg-amber-50" data-testid="upload-target-reminder">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Target tahun {tahun} belum diisi</AlertTitle>
          <AlertDescription className="text-amber-700">
            Sebaiknya isi/cek target {indikator} untuk tahun {tahun} di halaman <b>Kelola Target Tahunan</b> sebelum upload.
          </AlertDescription>
        </Alert>
      )}

      <Card className="border-slate-200 shadow-sm mb-6">
        <CardContent className="p-6">
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]);
            }}
            data-testid="upload-dropzone"
            className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-brand-sky hover:bg-brand-mist transition-colors"
          >
            <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-3" />
            {file ? (
              <p className="text-slate-700 font-medium">{file.name}</p>
            ) : (
              <>
                <p className="text-slate-700 font-medium">Klik atau seret file ke sini</p>
                <p className="text-sm text-slate-400 mt-1">Format .xlsx, .xls, atau .csv</p>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              data-testid="upload-file-input"
              onChange={(e) => setFile(e.target.files[0])}
            />
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={doPreview} disabled={loading || !file} data-testid="upload-preview-button">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Pratinjau Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <div className="space-y-6" data-testid="upload-preview">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
              <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{preview.total_baris}</div>
              <div className="text-sm text-slate-500">Total baris</div></CardContent></Card>
            <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div><div className="text-2xl font-extrabold text-emerald-700 tabular-nums" data-testid="preview-matched-count">{preview.jumlah_cocok}</div>
              <div className="text-sm text-emerald-600">Baris cocok</div></div></CardContent></Card>
            <Card className="border-rose-200 bg-rose-50"><CardContent className="p-4 flex items-center gap-3">
              <XCircle className="w-6 h-6 text-rose-600" />
              <div><div className="text-2xl font-extrabold text-rose-700 tabular-nums" data-testid="preview-unmatched-count">{preview.jumlah_gagal}</div>
              <div className="text-sm text-rose-600">Poliklinik tidak dikenali</div></div></CardContent></Card>
          </div>

          {preview.unmatched.length > 0 && (
            <Alert className="border-rose-300 bg-rose-50">
              <AlertTriangle className="h-4 w-4 text-rose-600" />
              <AlertTitle className="text-rose-800">Baris berikut gagal dicocokkan</AlertTitle>
              <AlertDescription className="text-rose-700">
                Nama poliklinik tidak ditemukan di master. Perbaiki nama atau tambahkan poliklinik di Kelola Master.
                <div className="mt-2 flex flex-wrap gap-2">
                  {preview.unmatched.map((u, i) => (
                    <Badge key={i} variant="outline" className="border-rose-300 text-rose-700">{u.nama_poliklinik}</Badge>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          )}

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-lg">Pratinjau ({preview.jumlah_cocok} baris akan disimpan)</CardTitle>
              <Button onClick={doConfirm} disabled={confirming || preview.jumlah_cocok === 0} data-testid="upload-confirm-button" className="bg-emerald-600 hover:bg-emerald-700">
                {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Konfirmasi & Simpan
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Departemen</TableHead>
                      <TableHead>Poliklinik</TableHead>
                      <TableHead className="text-right">N</TableHead>
                      <TableHead className="text-right">D</TableHead>
                      <TableHead className="text-right">Capaian</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.matched.map((m, i) => (
                      <TableRow key={i} data-testid={`preview-row-${i}`}>
                        <TableCell className="text-slate-500">{m.nama_departemen}</TableCell>
                        <TableCell className="font-medium">{m.nama_poliklinik}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.n ?? "-"}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.d ?? "-"}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{fmtPct(m.capaian)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
